import { useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import { ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { ExploreCard } from "@/components/ExploreCard"
import { CopyingTripOverlay } from "@/components/CopyingTripOverlay"
import { authClient } from "@/lib/auth-client"
import { trpc } from "@/lib/trpc-client"
import { trackEvent } from "@/components/Analytics"
import { useGuideLikes } from "@/lib/hooks/use-guide-likes"
import { useCopyGuide } from "@/lib/hooks/use-copy-guide"
import { guideSlug } from "@/lib/slugify"
import type { PublicGuideRow } from "@/lib/explore-data"

const PUBLIC_GUIDES_KEY = [`guides`, `public`] as const

// The grid's row type. PublicGuideRow (loader) and the trpc listPublic result
// diverge on `created_at` (Date when read straight off the row vs the string it
// serializes to over the wire), so widen it — the only field the grid reads as a
// date anyway. The trpc result is a superset (it still carries description_rich)
// and is structurally assignable to this; so is PublicGuideRow.
type ExploreGuide = Omit<PublicGuideRow, `created_at`> & {
  created_at: string | Date
}

// Mirrors the cta_click instrumentation on the other acquisition pages
// (AiTripPlannerPage / GroupTripPlannerPage): same event, `page` keyed to the
// route so the explore-page conversion CTA is measurable on its own.
function ctaClick(cta: string) {
  trackEvent(`cta_click`, { page: `trips-explore`, cta })
}

export function GuidePage({
  initialGuides,
}: {
  // Server-rendered published guides from the route loader. Seeds the query so
  // the grid is real <a href> markup in the SSR'd HTML (the SEO fix); React
  // Query then takes over for live like counts after hydration.
  initialGuides: PublicGuideRow[]
}) {
  const { data: session } = authClient.useSession()
  const { data: guides = [] } = useQuery<ExploreGuide[]>({
    queryKey: PUBLIC_GUIDES_KEY,
    queryFn: () => trpc.guides.listPublic.query(),
    initialData: initialGuides,
  })
  const { likedGuideIds, handleLikeGuide, handleUnlikeGuide } = useGuideLikes()
  const { copyingGuide, copyGuide } = useCopyGuide(`guide_page`)

  const sortedGuides = useMemo(
    () =>
      [...guides].sort(
        (a, b) =>
          new Date(b.created_at as unknown as string).getTime() -
          new Date(a.created_at as unknown as string).getTime()
      ),
    [guides]
  )

  const handleCopyGuide = (guideId: string) => {
    const guide = guides.find((g) => g.id === guideId)
    if (guide) void copyGuide(guide)
  }

  return (
    <div className="min-h-screen bg-background">
      <CopyingTripOverlay trip={copyingGuide} />
      <div className="px-5 md:px-8 pt-12 pb-20 max-w-7xl mx-auto">
        {/* Google crawled this page and rejected it as thin (2026-06-11 SEO
            audit): the rendered DOM was one word of heading plus a card grid.
            This intro is real on-page copy for the "travel guides &
            itineraries to copy" intent, not chrome — keep it text, not UI. */}
        <div className="mb-8 max-w-3xl">
          <h1 className="text-4xl font-bold mb-3">
            Explore travel guides &amp; itineraries
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed mb-2">
            Every guide below is a real, complete trip itinerary published by a
            ChicTravel traveler — day-by-day plans with every stop pinned on a
            live map, plus the notes, timings, and routes they actually used.
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Open any guide to read it free, no account needed. Found one that
            fits your trip? Copy it in one tap and the whole itinerary becomes
            your own editable plan — rearrange days, swap places, and invite
            your travel group to shape it with you.
          </p>

          {/* Primary conversion path for signed-out browsers: explore had a
              long tail of read-only sessions with no account prompt above the
              fold. Signed-in visitors already have a trip funnel in the header,
              so the CTA is anonymous-only to avoid redundant chrome. */}
          {!session && (
            <div className="mt-6">
              <Button
                size="lg"
                className="rounded-full px-7 h-12 text-base font-semibold shadow-md gap-2"
                asChild
              >
                <Link to="/sign-up" onClick={() => ctaClick(`hero`)}>
                  Plan your own trip free <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          )}
        </div>

        {/* No loading branch: the route loader seeds the grid via initialData,
            so there's real markup on first paint (SSR) and after hydration. */}
        {sortedGuides.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-border rounded-3xl">
            <h3 className="text-base font-bold mb-1">No published guides yet</h3>
            <p className="text-muted-foreground text-sm">
              Be the first to publish a guide.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(220px,1fr))] lg:grid-cols-4">
            {sortedGuides.map((guide) => (
              <ExploreCard
                key={guide.id}
                item={{
                  id: guide.id,
                  name: guide.name ?? null,
                  destination: guide.destination,
                  description: guide.description ?? null,
                  image_url: guide.image_url ?? null,
                  image_attribution: guide.image_attribution ?? null,
                  image_attribution_url: guide.image_attribution_url ?? null,
                  like_count: guide.like_count ?? 0,
                  view_count: guide.view_count ?? 0,
                  owner_name: guide.owner_name ?? undefined,
                  owner_image: guide.owner_image ?? undefined,
                  isLiked: likedGuideIds.has(guide.id),
                }}
                href={`/guide/${guideSlug(guide)}/${guide.short_id}`}
                onCopy={handleCopyGuide}
                onLike={handleLikeGuide}
                onUnlike={handleUnlikeGuide}
                isCopying={copyingGuide?.id === guide.id}
              />
            ))}
          </div>
        )}

        {/* Same thin-page fix as the intro: static explainer copy that crawls
            with the page regardless of how many guides the query returns. */}
        <div className="mt-16 pt-10 border-t border-border max-w-3xl space-y-6">
          <div>
            <h2 className="text-lg font-bold mb-2">
              What is a ChicTravel guide?
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              A guide is a finished trip, published. When a traveler nails
              their five days in Paris or two weeks across Japan, they can
              share the whole itinerary here — the places in order, the map
              with routes between stops, and their own notes on what was worth
              it. It&apos;s the difference between a top-ten list and a plan
              someone actually lived.
            </p>
          </div>
          <div>
            <h2 className="text-lg font-bold mb-2">
              Copy a guide, make it your trip
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The copy button on any guide duplicates the full itinerary into
              your own ChicTravel account as an editable trip. From there it
              behaves like any trip you built yourself: drag days around, add
              or remove places, set your dates, and plan together with your
              travel group in real time. Prefer to start from scratch? The{` `}
              <Link
                to="/ai-trip-planner"
                className="text-primary font-medium hover:underline"
              >
                free AI trip planner
              </Link>
              {` `}drafts a day-by-day itinerary from a sentence, and when your
              trip turns out great, you can publish it as the next guide on
              this page.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
